import { ref } from 'vue'

export type AiQueueStatus = {
    id: string
    status: string
    status_label: string
    queue_step: string | null
    queue_step_label: string | null
    latest_analysis_results: Array<{ model: string; confidence: number; reason: string }> | null
    latest_extraction_results: Record<string, unknown> | null
    latest_review_results: Record<string, unknown> | null
    latest_finalize_results: Record<string, unknown> | null
    confirmed_model_type: string | null
    processing_errors: Record<string, unknown> | null
    retry_count: number
    created_at: string
    updated_at: string
}

export type ProcessingStep = 'analysis' | 'extraction' | 'review' | 'finalization'

const STEP_ORDER: Record<string, number> = {
    awaiting_ai_classification: 1,
    awaiting_model_confirmation: 1,
    awaiting_extraction: 2,
    awaiting_ai_review: 3,
    awaiting_user_review: 3,
    awaiting_finalization: 4,
    completed: 5,
}

export function useAiUpload() {
    const isUploading = ref(false)
    const queueId = ref<string | null>(null)
    const queueStatus = ref<AiQueueStatus | null>(null)
    const error = ref<string | null>(null)

    let pollInterval: ReturnType<typeof setInterval> | null = null

    function getCsrfToken(): string {
        return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
    }

    async function uploadFiles(
        files: File[],
        modelConfigKey: string,
        targetModelClass: string,
    ): Promise<void> {
        isUploading.value = true
        error.value = null
        queueId.value = null
        queueStatus.value = null

        const formData = new FormData()
        files.forEach((file) => formData.append('files[]', file))
        formData.append('model_config_key', modelConfigKey)
        formData.append('target_model_class', targetModelClass)
        formData.append('auto_process', 'true')

        try {
            const response = await fetch('/api/ai-processing/queue/upload', {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': getCsrfToken() },
                body: formData,
            })

            const data = await response.json()

            if (!response.ok || !data.success) {
                throw new Error(data.message ?? `Upload failed (${response.status})`)
            }

            queueId.value = data.data.id
            queueStatus.value = data.data
            startPolling()
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Upload failed'
            isUploading.value = false
        }
    }

    async function refreshStatus(): Promise<void> {
        if (!queueId.value) {
            return
        }

        try {
            const response = await fetch(`/api/ai-processing/queue/${queueId.value}`, {
                headers: { Accept: 'application/json' },
            })
            const data = await response.json()

            if (data.success) {
                queueStatus.value = data.data
            }
        } catch {
            // Silently ignore polling errors; the interval will retry
        }
    }

    function startPolling(): void {
        stopPolling()
        pollInterval = setInterval(() => {
            void (async () => {
                await refreshStatus()

                const status = queueStatus.value?.status
                const step = queueStatus.value?.queue_step

                if (status === 'completed' || status === 'failed' || status === 'cancelled') {
                    stopPolling()
                    isUploading.value = false
                } else if (status === 'awaiting_user_action' && step === 'awaiting_model_confirmation') {
                    stopPolling()
                    // Leave isUploading true — user must act
                }
            })()
        }, 2000)
    }

    function stopPolling(): void {
        if (pollInterval !== null) {
            clearInterval(pollInterval)
            pollInterval = null
        }
    }

    async function confirmModelType(modelType: string): Promise<void> {
        if (!queueId.value) {
            return
        }

        isUploading.value = true

        try {
            const response = await fetch(`/api/ai-processing/queue/${queueId.value}/confirm`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                    Accept: 'application/json',
                },
                body: JSON.stringify({ model_type: modelType }),
            })

            const data = await response.json()

            if (!response.ok || !data.success) {
                throw new Error(data.message ?? 'Confirmation failed')
            }

            if (data.data) {
                queueStatus.value = data.data
            }

            startPolling()
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Confirmation failed'
            isUploading.value = false
        }
    }

    async function retryProcessing(): Promise<void> {
        if (!queueId.value) {
            return
        }

        isUploading.value = true
        error.value = null

        try {
            const response = await fetch(`/api/ai-processing/queue/${queueId.value}/retry`, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': getCsrfToken(),
                    Accept: 'application/json',
                },
            })

            const data = await response.json()

            if (!response.ok || !data.success) {
                throw new Error(data.message ?? 'Retry failed')
            }

            if (data.data) {
                queueStatus.value = data.data
            }

            startPolling()
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Retry failed'
            isUploading.value = false
        }
    }

    function getActiveStep(): number {
        const step = queueStatus.value?.queue_step
        if (!step) {
            return 0
        }

        return STEP_ORDER[step] ?? 0
    }

    function reset(): void {
        stopPolling()
        queueId.value = null
        queueStatus.value = null
        error.value = null
        isUploading.value = false
    }

    return {
        isUploading,
        queueId,
        queueStatus,
        error,
        uploadFiles,
        confirmModelType,
        retryProcessing,
        refreshStatus,
        getActiveStep,
        reset,
    }
}
